import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const PriorityDistributionChart = () => {
  const priorityData = [
    { name: 'Critical', value: 12, color: '#dc2626', icon: 'AlertTriangle' },
    { name: 'High', value: 28, color: '#f59e0b', icon: 'AlertCircle' },
    { name: 'Medium', value: 45, color: '#3b82f6', icon: 'Info' },
    { name: 'Low', value: 31, color: '#10b981', icon: 'CheckCircle' }
  ];

  const total = priorityData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = ((data.value / total) * 100).toFixed(1);
      
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-3">
          <div className="flex items-center space-x-2 mb-1">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: data.color }}
            />
            <span className="text-sm font-medium text-text-primary">{data.name}</span>
          </div>
          <p className="text-sm text-text-secondary">
            {data.value} tickets ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null;

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="500"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 dashboard-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
          <Icon name="PieChart" size={20} className="text-warning" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Priority Distribution</h3>
          <p className="text-sm text-text-secondary">Current ticket priorities</p>
        </div>
      </div>

      <div className="h-64 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={priorityData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {priorityData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-3">
        {priorityData.map((item) => {
          const percentage = ((item.value / total) * 100).toFixed(1);
          return (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  />
                  <Icon name={item.icon} size={16} style={{ color: item.color }} />
                </div>
                <span className="text-sm font-medium text-text-primary">{item.name}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-text-primary">{item.value}</div>
                <div className="text-xs text-text-secondary">{percentage}%</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-secondary">Total Open Tickets</span>
          <span className="font-semibold text-text-primary">{total}</span>
        </div>
      </div>
    </div>
  );
};

export default PriorityDistributionChart;